import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { CheckCircle2, XCircle, HelpCircle, ExternalLink, Phone, Mail, MapPin, Building2, Calendar, Sparkles, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const STATUS_META = {
  VERIFIED:     { icon: CheckCircle2, cls: "priority-low" },
  NOT_DETECTED: { icon: XCircle,      cls: "priority-high" },
  UNKNOWN:      { icon: HelpCircle,   cls: "priority-medium" },
};

const StatusBadge = ({ status }) => {
  const { t } = useI18n();
  const Icon = STATUS_META[status]?.icon || HelpCircle;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase ${STATUS_META[status]?.cls}`}>
      <Icon size={11} strokeWidth={2.4} />
      {t(`results.status.${status}`)}
    </span>
  );
};

const factLabel = (t, fact) => {
  const tl = t(`results.factLabels.${fact.key}`);
  return tl && tl !== `results.factLabels.${fact.key}` ? tl : fact.label;
};

const KV = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-md bg-[color:var(--impact-bg)] border border-[color:var(--impact-border)] grid place-items-center shrink-0">
      <Icon size={14} className="text-[color:var(--impact-ink-2)]" />
    </div>
    <div className="min-w-0">
      <div className="eyebrow mb-0.5">{label}</div>
      <div className="text-sm break-words">{children}</div>
    </div>
  </div>
);

export const AdminAuditModal = ({ auditId, open, onOpenChange }) => {
  const { t, lang, setLang } = useI18n();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !auditId) return;
    let cancelled = false;
    setLoading(true);
    setDoc(null);
    setError(null);
    api.get(`/admin/audits/${auditId}`)
      .then((r) => { if (!cancelled) setDoc(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.status === 404 ? "not_found" : "load_error"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, auditId]);

  const retry = () => {
    if (!auditId) return;
    setLoading(true); setError(null);
    api.get(`/admin/audits/${auditId}`)
      .then((r) => setDoc(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "load_error"))
      .finally(() => setLoading(false));
  };

  const facts = doc?.result?.facts || [];
  const cats = doc?.result?.category_scores || [];
  const narratives = doc?.result?.category_narratives || [];
  const narrativesByKey = useMemo(
    () => Object.fromEntries(narratives.map((n) => [n.key, n])),
    [narratives]
  );
  const factsByCat = useMemo(() => {
    const g = {};
    for (const f of facts) (g[f.category] = g[f.category] || []).push(f);
    return g;
  }, [facts]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="admin-audit-modal"
        className="max-w-5xl w-[95vw] p-0 gap-0 max-h-[92vh] overflow-hidden bg-white"
      >
        <DialogHeader className="px-8 pt-8 pb-5 border-b border-[color:var(--impact-border)]">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="font-display text-2xl tracking-tight">
                {doc?.company?.company_name || (loading ? "…" : t("adminModal.title"))}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {t("adminModal.title")}
              </DialogDescription>
              {doc?.company?.website_url && (
                <a href={doc.company.website_url} target="_blank" rel="noreferrer"
                   className="text-xs text-[color:var(--impact-brand)] animated-underline inline-flex items-center gap-1 mt-1">
                  <ExternalLink size={11} /> {doc.company.website_url}
                </a>
              )}
            </div>
            <button
              data-testid="admin-modal-lang-toggle"
              aria-label={t("lang.switchTo")}
              onClick={() => setLang(lang === "fr" ? "en" : "fr")}
              className="shrink-0 mr-8 text-xs font-bold tracking-[0.18em] px-3 py-2 border border-[color:var(--impact-border)] rounded-full hover:bg-[color:var(--impact-bg)] transition-colors"
            >
              {lang === "fr" ? "EN" : "FR"}
            </button>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto p-8 space-y-8" style={{ maxHeight: "calc(92vh - 96px)" }}>
          {loading && (
            <div className="text-sm text-[color:var(--impact-ink-2)]">{t("adminModal.loading")}</div>
          )}
          {error && !loading && (
            <div data-testid="admin-modal-error" className="flex items-start gap-3 p-4 rounded-lg border border-[color:var(--impact-high)] bg-[color:var(--impact-high)]/5">
              <AlertTriangle size={16} className="mt-0.5 text-[color:var(--impact-high)] shrink-0" />
              <div className="flex-1">
                <div className="font-semibold text-sm">
                  {error === "not_found" ? t("adminModal.errorNotFound") : t("adminModal.errorGeneric")}
                </div>
                <button onClick={retry} className="mt-2 text-xs font-bold text-[color:var(--impact-brand)] animated-underline">
                  {t("adminModal.retry")}
                </button>
              </div>
            </div>
          )}
          {doc && !error && (
            <>
              {/* Overview */}
              <section className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-3">
                  <KV icon={Building2} label={t("adminModal.industry")}>{doc.company.industry}</KV>
                  <KV icon={MapPin} label={t("adminModal.city")}>{doc.company.city}</KV>
                  <KV icon={Calendar} label={t("adminModal.createdAt")}>{new Date(doc.created_at).toLocaleString()}</KV>
                  <KV icon={Sparkles} label={t("adminModal.objective")}>{doc.objective}</KV>
                </div>
                <div className="space-y-3">
                  <KV icon={Building2} label={t("adminModal.contact")}>
                    <div>{doc.contact.first_name} {doc.contact.last_name}</div>
                  </KV>
                  <KV icon={Mail} label={t("admin.email")}>{doc.contact.email}</KV>
                  <KV icon={Phone} label={t("adminModal.phone")}>{doc.contact.phone}</KV>
                  <KV icon={Sparkles} label={t("adminModal.ads")}>
                    {doc.ads?.status}{doc.ads?.monthly_spend ? ` · ${doc.ads.monthly_spend}` : ""}
                  </KV>
                </div>
              </section>

              {/* Executive summary */}
              {doc.result?.executive_summary && (
                <section>
                  <div className="eyebrow mb-2">{t("results.execSummary")}</div>
                  <p className="text-[15px] leading-relaxed text-[color:var(--impact-ink)]">{doc.result.executive_summary}</p>
                </section>
              )}

              {/* Category scores */}
              <section>
                <div className="eyebrow mb-3">{t("results.catTitle")}</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {cats.map((c) => (
                    <div key={c.key} className="p-4 rounded-lg border border-[color:var(--impact-border)] bg-[color:var(--impact-bg)]">
                      <div className="eyebrow text-[10px] mb-1">{t(`results.dimensions.${c.key}`)}</div>
                      <div className="font-display font-black text-3xl tabular-nums leading-none mb-1">
                        {c.score === null || c.score === undefined ? "—" : c.score}
                      </div>
                      <div className="text-[11px] text-[color:var(--impact-ink-2)]">
                        {c.verified_count}/{c.coverage_checked} · {c.unknown_count} {t("adminModal.unknownShort")}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Facts by category */}
              <section>
                <div className="eyebrow mb-3">{t("results.factsTitle")}</div>
                <div className="space-y-6">
                  {cats.map((c) => {
                    const list = factsByCat[c.key] || [];
                    const n = narrativesByKey[c.key];
                    return (
                      <div key={c.key} className="border border-[color:var(--impact-border)] rounded-lg overflow-hidden">
                        <div className="px-4 py-3 bg-[color:var(--impact-bg)] flex items-center justify-between">
                          <div>
                            <div className="font-display font-bold text-[15px]">{t(`results.dimensions.${c.key}`)}</div>
                            <div className="text-[11px] text-[color:var(--impact-ink-2)]">
                              {t("results.coverageTemplate")
                                .replace("{checked}", c.coverage_checked)
                                .replace("{total}", c.coverage_total)}
                            </div>
                          </div>
                          {n?.priority && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider priority-${n.priority}`}>
                              {t(`results.priority.${n.priority}`)}
                            </span>
                          )}
                        </div>
                        <div className="divide-y divide-[color:var(--impact-border)]">
                          {list.map((f) => (
                            <div key={f.key} className="px-4 py-3" data-testid={`admin-fact-${f.key}`}>
                              <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2 flex-wrap min-w-0">
                                  <div className="font-semibold text-[13px] break-words">{factLabel(t, f)}</div>
                                  <StatusBadge status={f.status} />
                                  {f.confidence !== "high" && (
                                    <span className="text-[10px] uppercase tracking-widest text-[color:var(--impact-ink-2)]">
                                      {f.confidence === "medium" ? t("adminModal.confMedium") : t("adminModal.confLow")}
                                    </span>
                                  )}
                                </div>
                                {f.source_url && (
                                  <a href={f.source_url} target="_blank" rel="noreferrer"
                                     className="text-[11px] text-[color:var(--impact-brand)] inline-flex items-center gap-1 truncate max-w-[280px]">
                                    <ExternalLink size={11} /> {f.source_url.replace(/^https?:\/\//, "")}
                                  </a>
                                )}
                              </div>
                              {(f.value !== null && f.value !== undefined && f.status === "VERIFIED") && (
                                <div className="text-[12px] text-[color:var(--impact-ink-2)] mt-1 break-words">
                                  {typeof f.value === "object" ? JSON.stringify(f.value) : String(f.value)}
                                </div>
                              )}
                              {f.evidence && (
                                <div className="text-[12px] text-[color:var(--impact-ink)] mt-2 p-2 rounded bg-[color:var(--impact-bg)] border border-[color:var(--impact-border)] break-words">
                                  {f.evidence}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Recommendations */}
              {doc.result?.recommendations?.length > 0 && (
                <section>
                  <div className="eyebrow mb-3">{t("results.recTitle")}</div>
                  <ul className="space-y-2">
                    {doc.result.recommendations.map((r, i) => (
                      <li key={i} className="p-3 rounded-lg border border-[color:var(--impact-border)]">
                        <div className="font-semibold text-[14px]">
                          {t(`results.services.${r.service}`) === `results.services.${r.service}` ? r.label : t(`results.services.${r.service}`)}
                        </div>
                        <div className="text-[13px] text-[color:var(--impact-ink-2)] mt-1">{r.reason}</div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Crawl / meta */}
              <section>
                <div className="eyebrow mb-2">{t("adminModal.crawl")}</div>
                <div className="text-[12px] text-[color:var(--impact-ink-2)] space-y-1">
                  <div>{t("adminModal.homeUrl")}: <span className="text-[color:var(--impact-ink)]">{doc.result?.crawl?.home_url}</span></div>
                  <div>{t("adminModal.reachable")}: {doc.result?.crawl?.reachable ? "✓" : "✗"}</div>
                  <div>{t("adminModal.pagesAnalyzed")}: {doc.result?.crawl?.pages_analyzed_count}</div>
                  <div>{t("adminModal.leadScore")}: {doc.lead_score} · {doc.lead_quality}</div>
                  <div>{t("adminModal.hubspot")}: {doc.hubspot_synced ? "✓" : "—"}</div>
                </div>
              </section>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
